<template>
  <div>
    <span id="1-applications-summary-page" class="anchor"></span>
    <h3>1. Applications Summary Page</h3>
    <p>
      This page shows a summary of all the applications' health. As
      shown in Fig. 8.1, the tiles display high-level overview of the
      applications' health over the selected time period (Fig. 8). The tile is
      highlighted with "orange" to signify performance alerts which the
      xVisor-user could potentially review; if there is no such activity/alert, the
      tile will be highlighted as "green". The tiles also show alert
      distributions (in bar chart) in different time buckets. More information on
      each of these applications can be accessed by clicking the "Details"
      button on the summary tile.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-applications-page.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 8.1: Ennetix xVisor Applications page.</p>
    <p>
      This page also shows alerts summaries of all applications as shown in Fig. 8.2.
      xVisor-users can check applications' status and events details by click on the
      "orange" event button. There is a search bar on the right top which allows
      xVisor-users to search alerts by keyword.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-applications-page2.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 8.2: Ennetix xVisor Applications page.</p>
    <span id="2-application-details-page" class="anchor"></span>
    <h3>2. Application Details Page</h3>
    <p>
      Clicking on the Application Summary tile's "Details" button for
      a specific application (Fig. 8) reveals the Application Details page. The
      detail page is the starting point of all performance analytics information
      related to the application and its end-users' activities. One can go back
      in time to view historical performances, alerts/events as well by selecting
      the relevant time frame in time selector (top right of Fig. 9a). Fig. 9b shows
      the time-selector panel. Ennetix xVisor keeps 5-min granularity data for each
      application monitored for up to a month. If needed, xVisor can keep this data
      (of 5-min or a different granularity) even for longer time period.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-application-detail-page.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 9a: Ennetix xVisor Application Details page - top panel.</p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-application-detail-page-time-selector.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 9b: Ennetix xVisor Application Details page - time selector.</p>
    <p>
      The top of the "Application Details" page shows three panels which
      give stats on alerts, alert distribution by zone (which is a physical area of
      the client's network such as building, floor, etc.), and status of network
      services relevant to the application (Fig. 9a). xVisor-user can click on any
      of the bars or boxes (of alert counts) on these panels and get information on
      all the alerts of that type in the alert panel below the time bar (Fig. 9c).
      More on this alert panel can be found in Section 2.4.7.
    </p>
    <p>
      After all the top panels, there is a time bar (Fig. 9c) which shows alerts and
      events that occurred for the application over the selected timeframe (default
      set to past six hours). If there is no alert for a time bar, that bar will be
      in "gray" color. xVisor-user can click on any of the bars with
      alerts (highlighted by purple, orange, or red colors; Note: these colors
      signify alert severities, with purple being lowest-severity alert (called
      "notice"), orange being "warning" alert, and red being
      "critical" alert), and get information on alerts that happened
      during that time. The alert information will appear in a box below the time
      bar (more on this can be found on Section 2.4.7). The current time selector on
      the time bar is shown as a "white ball" on top of the corresponding
      time bar.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-application-detail-page-time-bar.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 9c: Ennetix xVisor Application Details page - time bar.</p>
    <p>
      As shown in Fig 9c, just below the time bar, xVisor Application Details page
      has multiple tabs where various information are presented on the application
      and end-user performance. These tabs are: (a) Overview, (b) Users/Devices, (c)
      Transactions, (d) Flows, (e) Network. Sections 2.4.1 - 2.4.6 present details
      on each of these tabs.
    </p>
    <span id="2-1-overview-tab" class="anchor"></span>
    <h4>2.1. Overview Tab</h4>
    <p>
      The Overview tab presents a performance overview of the
      application. It presents a components overview table (Fig. 10a1) to show all related components
      of the application, clicking on the purple button "Class Rules Details" will show
      traffic rules related to the current component in a prompt table (Fig. 10a2).
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-comp-table.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 10a1: Ennetix xVisor Application Details page - Overview tab - Components Table.</p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-class-rules-table.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 10a2: Ennetix xVisor Application Details page - Overview tab - Class Rules Details.</p>
    <p>
      Clicking on a row of the components table will show a zones table under the current table
      for more details about zone information.
      (Fig. 10b).
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-zone-table.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 10b: Ennetix xVisor Application Details page - Zones Table.</p>

    <span id="2-2-users-tab" class="anchor"></span>
    <h4>2.2. Users Tab</h4>
    <p>
      This is the most important tab on Detailed Application page. Figs. 11a - 11d
      present various aspects of this tab. On this tab, a list of all application
      end-users is given (Fig. 11a). Using the search feature of the panel, specific
      end-user/device can be found, and end-users from one zone can be selected
      (Fig. 11a).
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-application-detail-page-users-tab.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 11a: Ennetix xVisor Applications Details page - Users tab (part 1).</p>
    <p>
      To review performance information and analytics for each end-user, clicking
      on each row will expand extra details below corresponding to the user's
      performance and analytics. As shown in Fig. 11b, end-user 128.120.32.227
      is selected and expanded to show detailed performance analytics specific
      to the end-user.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-application-detail-page-users-tab2.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 11b: Ennetix xVisor Applications Details page - Users tab (part 2).</p>
    <span id="2-3-transactions-tab" class="anchor"></span>
    <h4>2.3. Transactions Tab</h4>
    <p>
      All application transactions for the selected time period are displayed in
      Transactions tab (as shown in Fig. 12a). Tabs separate different application
      components. There is a time series chart on the left shows all transactions counts in a time range.
      Various statistical insights on application transactions and their abnormalities
      appear on the "Insights" panel of this tab. These insights give xVisor-user
      critical information on overall performance of the application; and
      irregularities, abnormalities, and things to look for in regards to the
      application.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-transactions.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 12a: Ennetix xVisor Application Details page - Transactions tab (Part-1).</p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-transactions-stats.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 12b: Ennetix xVisor Application Details page - Transactions tab (Part-2).</p>
    <span id="2-4-flows-tab" class="anchor"></span>
    <h4>2.4. Flows Tab</h4>
    <p>
      As shown in Fig. 13, the flow radial diagram shows for each application
      component (separated by tabs) the incoming and outgoing flows for the
      application at each time period (5-min default). There is a time bar with
      which xVisor-user can select specific time period and see the flow data for
      that specific time period. Again, selected period is highlighted by the white
      ball over the corresponding time bar (Fig. 13). The application is represented
      by the orange bar and different flows are represented by the chords in/out to
      the application. For example, one of the chords shows that 13.4 MB of data was
      sent from the "MS Teams" application to the end-user 128.120.25.1,
      while the application received 445.1 KB of data from the same end-user.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-flows.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 13: Ennetix xVisor Application Details page - Flows tab (Part-1).</p>
    <p>
      More information such as bytes, packets, average packet size, etc. can be
      found by hovering on each of the chords of flows on the radial diagram (Fig.
      13). Analytical insights and abnormalities on flow statistics can be found on
      the "Insights" panel beside the radial flow diagram. These insights
      are useful to figure out abnormal behavior of end-users of the application.
      Flow and end-user statistics per application server can also be found on the
      "Flows" tab just below the radial diagram (Fig. 14). Abnormal events
      (based on flow analysis of end-user's behavior) will also show in this
      radial diagram.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-application-detail-page-flows-tab2.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 14: Ennetix xVisor Application Details page - Flows tab (Part-2).</p>
    <span id="2-5-network-tab" class="anchor"></span>
    <h4>2.5. Network Tab</h4>
    <p>
      This tab shows the combined view of all the application-service pathroutes and
      network-specific characteristics such as delay, jitter, and bandwidth, when
      the application is accessed from different zones (which could be
      geographically distributed). This is a Google-map like view where client can
      easily find out which hop/link on the network is creating problem for the
      end-users of the application. As shown in Fig. 15a, all the network paths
      (along with hop-by-hop performance) and their end-to-end characteristics
      (shown in the charts below the network topology) are presented in this tab.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-application-detail-page-network-tab.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 15a: Ennetix xVisor Application Details page - Network tab (Part 1).</p>
    <p>
      At the top of the page, there is a Path Health/Performance bar (Fig. 15a).
      This bar shows alerts/events that got generated for the selected time period
      using various colors. If one of the bars is gray, that means there is no
      "network" performance alerts/events. By clicking on any bar, the
      xVisor-user can select a specific time slot (highlighted by white ball over
      the time bar). The pathroute view on this page will highlight data at that
      time period.
    </p>
    <p>
      The xVisor-user can enhance the visualization of the pathroute view by
      clicking on each individual node. Differing colors indicate different domains
      within the route; i.e., in Fig. 15a, yellow domain indicates all the routers
      within "ASN 6192" domain, and blue indicates all the routers within
      Amazon (ASN 16509) domain. For each node, information on delay and loss
      statistics (such as max, min, average, baseline) along with interface
      information (such as name and IP) can be found on hovering tooltip (Fig. 15b).
      Also, various performance events will be shown as red color on links/nodes of
      this pathroute visualization. If any link/node is having performance issues
      (such as high loss and/or delay), it will show as a red line/border to signify
      the issue (Fig. 15a). Each of these nodes can also be clicked to look at more
      information on individual "Network Element" page (Section 2.6) (if
      such information is available).
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-application-detail-page-network-tab2.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 15b: Ennetix xVisor Application Details page - Network tab (Part 2).</p>
    <p>
      Below the Pathroutes, there is a table (called "Probe Status")
      showing all the active probes that are collecting network performance data
      from different zones to the application and service components (Fig. 15c).
      Each row presents one probe with information on health, source XOME, Target
      Application/Service component, Zone, and number of alerts for the time period.
      The health of the probe indicates whether the network performance is good/bad
      (red is bad, yellow is alarming, and green is good). Again, health is a
      combined index of all the performance data collected by that probe, and is
      represented on a scale of 0-10 (10 being best or most healthy).
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-application-detail-page-network-tab3.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 15c: Ennetix xVisor Application Details page - Network tab (Part 3).</p>
    <p>
      For each of the probe rows, there is a ">" which give the visual
      representation of various performance profiles of the probe. These performance
      profiles can be, at network layer: delay, jitter, bandwidth; and at
      application layer: HTTP/S profiles, DNS profiles, etc.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-application-detail-page-network-tab4.png"
        class="responsive-img-lg"
      />
    </p>
    <p>
      Fig. 15d: Ennetix xVisor Application Details page - Network tab (probe
      performance profiles).
    </p>
    <p>
      There is a tab option to view the distribution of the probe performance
      through packet delay variation, also known as jitter. The xVisor-user can
      access "Packet Delay Variation" by clicking the tab for jitter. This
      type of variation tracks only the performance of individual delay probe
      packets. Hovering over the distribution provides specific numbers for each
      delay performance statistic. There is another tab on this same view, which
      shows the statistics on performance data such as Available Bandwidth.
      Bandwidth is measured in Mbps (megabits per second), which assesses the
      available transfer speed between the source and the destination of the network
      path of the probe.
    </p>
    <p>
      The Application performance panel (Fig. 15d) tracks the performance of
      applications running on top of the network path. Several specific
      applications' performance profiles can be displayed, such as HTTP, HTTPs,
      DNS, etc. HTTP/HTTPs performance profile data can be distributed over various
      steps of HTTP/S application performance, such as name lookup, connect, SSL,
      response, and fetch time (Fig. 15d). The xVisor-user can utilize this
      application profile to view and compare the performance of specific
      applications. Hovering over particular points of the distribution can provide
      more information of each application's performance on the path. The events
      distribution panel on the right (Fig. 15d) shows the distribution of events
      for that probe during the time period.
    </p>
    <span id="2-6-alerts-panel" class="anchor"></span>
    <h4>2.6. Alerts Panel</h4>
    <p>
      This panel shows the alerts (generated for the application) along with all the
      relevant information for a specific time. The "Alerts" panel is a
      box (with alerts organized in a swiper carousel) (Fig. 17a) that appears below
      the time bar when xVisor-user clicks on - (a) a bar of the top time bar (Fig.
      9c) of the application page, (b) the alert stats bars of overview page top
      three panels (Fig. 6a), or (c) the alert stats bars at the top of
      "Application" page (Fig. 9a). As shown in Fig. 17a, when the orange
      bar is clicked, the carousel panel appears and shows details of the alerts
      generated for that time slot. Each alert will have a badge showing its type
      (in Fig. 17a, it is of "Service" type) and severity (shown in orange
      in Fig. 17a), along with a message containing details of the alerts. If there
      are multiple alerts available for a specific time slot, they will show up as
      pages inside the alert box (Fig. 17b). If there are multiple carousels of
      alerts for multiple time slots, xVisor-user can swipe through them to look at
      those alerts (Figs. 17a, 17b).
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-application-detail-page-alerts-panel.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 17a: Ennetix xVisor Applications Details page - Alerts panel (Part 1).</p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-application-detail-page-alerts-panel2.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 17b: Ennetix xVisor Applications Details page - Alerts panel (Part 2).</p>
    <p>
      When xVisor-user wants to explore more on a specific alert, he/she can click
      on the "Details" of the alert message (Figs. 17a, 17b). Then, a
      message box appears with details of the alert along with "Troubleshooting
      Hint(s)" and "Next Step(s)" to follow to mitigate the
      performance issue (Fig. 17c).
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-application-detail-page-alerts-panel3.png"
        class="responsive-img-lg"
      />
    </p>
    <p>
      Fig. 17c: Ennetix xVisor Applications Details page - Alerts panel
      troubleshooting and next steps.
    </p>
    <p>
      The "Details" on "Troubleshooting Hint(s)" (Fig. 17c) will
      help xVisor-user to navigate to specific "tabs" and get more details
      on performance issue. For example, if the performance issue is captured by a
      performance probe on the "Network" tab, clicking the
      "Details" on "Troubleshooting Hint(s)" will switch to
      "Network" tab and highlight the probe that shows the performance
      issue in detail (Fig. 17d). Similarly, if end-users are getting affected by
      the alert, clicking "Details" may open the "Users" tab and
      show all the end-users highlighted.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-application-detail-page-alerts-panel4.png"
        class="responsive-img-lg"
      />
    </p>
    <p>
      Fig. 17d: Ennetix xVisor Applications Details page - Alerts panel
      troubleshooting and next steps.
    </p>
  </div>
</template>

<script>
export default {
  data() {
    return {
      rootLink: window.location.hostname,
    };
  },
};
</script>

<style scoped>
.anchor {
  top: -75px;
  position: relative;
  z-index: 5;
}
</style>
